'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const STORAGE_KEY = 'cookie-consent';

interface CookieConsentState {
  analytics: boolean;
  marketing: boolean;
  showBanner: boolean;
}

interface CookieConsentContextValue extends CookieConsentState {
  acceptAll: () => void;
  rejectAll: () => void;
  acceptSelected: (categories: {
    analytics: boolean;
    marketing: boolean;
  }) => void;
  resetConsent: () => void;
  exempt: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(
  null,
);

function loadConsent(): CookieConsentState | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      analytics: boolean;
      marketing: boolean;
    };
    return {
      analytics: parsed.analytics ?? false,
      marketing: parsed.marketing ?? false,
      showBanner: false,
    };
  } catch {
    return null;
  }
}

function saveConsent(analytics: boolean, marketing: boolean) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ analytics, marketing }));
}

function dispatchConsentEvent(analytics: boolean, marketing: boolean) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('cookie-consent-update', {
      detail: { analytics, marketing },
    }),
  );
}

export function CookieConsentProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<CookieConsentState>({
    analytics: false,
    marketing: false,
    showBanner: false,
  });
  const exemptedRef = useRef(false);

  useEffect(() => {
    const saved = loadConsent();
    if (saved) {
      setState(saved);
      dispatchConsentEvent(saved.analytics, saved.marketing);
    } else if (!exemptedRef.current) {
      setState((prev) => ({ ...prev, showBanner: true }));
    }
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent(true, true);
    setState({ analytics: true, marketing: true, showBanner: false });
    dispatchConsentEvent(true, true);
  }, []);

  const rejectAll = useCallback(() => {
    saveConsent(false, false);
    setState({ analytics: false, marketing: false, showBanner: false });
    dispatchConsentEvent(false, false);
  }, []);

  const acceptSelected = useCallback(
    (categories: { analytics: boolean; marketing: boolean }) => {
      saveConsent(categories.analytics, categories.marketing);
      setState({
        analytics: categories.analytics,
        marketing: categories.marketing,
        showBanner: false,
      });
      dispatchConsentEvent(categories.analytics, categories.marketing);
    },
    [],
  );

  const resetConsent = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
    setState({ analytics: false, marketing: false, showBanner: true });
  }, []);

  const exempt = useCallback(() => {
    exemptedRef.current = true;
    // Exempt jurisdictions (e.g. POPIA) require no consent: tracking
    // categories are allowed by default. A saved explicit choice from a
    // non-exempt surface still wins — the load effect runs after this and
    // overwrites with the stored preference. Not persisted.
    setState((prev) => ({
      ...prev,
      analytics: true,
      marketing: true,
      showBanner: false,
    }));
    dispatchConsentEvent(true, true);
  }, []);

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      ...state,
      acceptAll,
      rejectAll,
      acceptSelected,
      resetConsent,
      exempt,
    }),
    [state, acceptAll, rejectAll, acceptSelected, resetConsent, exempt],
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextValue {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      'useCookieConsent must be used within a CookieConsentProvider',
    );
  }
  return context;
}
